import { Icon, IconType, StyleUtils } from '@main/core-ui';
import type { RequestHistory } from '@main/dsr-types';
import DownloadRequest from '@main/privacy-center/src/containers/DownloadRequest';
import RevokeRequest from '@main/privacy-center/src/containers/RevokeRequest';
import type { DownloadZipProps } from '@main/privacy-center/src/hooks';
import { sp } from '@main/theme';
import React from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import Moment from 'react-moment';

import {
  ButtonPanelWrapper,
  ButtonWrapper,
  DescriptionWrapper,
  IconWrapper,
  RequestPreviewWrapper,
  SideWrapper,
  TextWrapper,
  TimestampWrapper,
  TitleWrapper,
} from './wrappers';

/**
 * The props that can be customized
 */
export interface CustomStatusBlockProps extends DownloadZipProps {
  /** Title of the status block */
  title: MessageDescriptor;
  /** Link to icon of the status block */
  icon: IconType;
  /** Description of the status */
  description: MessageDescriptor;
}

/**
 * The StatusBlock props
 */
export interface StatusBlockProps
  extends RequestHistory,
    CustomStatusBlockProps {}

/**
 * Sub-component of RequestPreview, one rendered block of an active request
 */
export function StatusBlock({
  id,
  title,
  action,
  icon,
  description,
  canDownload,
  zipFileName,
  report,
  canRevoke,
  createdAt,
  secondaryStartTime,
  partition,
  downloadZip,
  downloadStatus,
}: StatusBlockProps): JSX.Element {
  const { formatMessage } = useIntl();
  return (
    <RequestPreviewWrapper>
      <SideWrapper>
        <IconWrapper style={{ fontSize: `${sp(4.5)}` }}>
          <Icon type={icon} size={sp(5)} />
        </IconWrapper>
      </SideWrapper>
      <TextWrapper>
        <TitleWrapper>{formatMessage(title)}</TitleWrapper>
        <TimestampWrapper>
          <Moment format="dddd, MMM D YYYY, hh:mm a">{createdAt}</Moment>
        </TimestampWrapper>
        <DescriptionWrapper>
          {formatMessage(description, {
            title: (
              <strong
                style={{ display: 'inline-flex', gap: StyleUtils.Spacing.sm }}
              >
                <span>{formatMessage(action.title)}</span>
                {partition && (
                  <>
                    <span>-</span>
                    <span>{partition.name}</span>
                  </>
                )}
              </strong>
            ),
            time: secondaryStartTime ? (
              <Moment fromNow>{secondaryStartTime}</Moment>
            ) : (
              ''
            ),
          })}
        </DescriptionWrapper>
        <ButtonPanelWrapper>
          {canDownload && (
            <ButtonWrapper>
              <DownloadRequest
                report={report?.template || ''}
                zipFileName={zipFileName}
                requestId={id}
                downloadZip={downloadZip}
                downloadStatus={downloadStatus}
              />
            </ButtonWrapper>
          )}
          {canRevoke && (
            <ButtonWrapper>
              <RevokeRequest id={id} />
            </ButtonWrapper>
          )}
        </ButtonPanelWrapper>
      </TextWrapper>
    </RequestPreviewWrapper>
  );
}
